/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React, { useRef, memo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import { TimeUnit } from 'enums/Charts'
import { AnalysisType, InsightViewOption, NBColor, YAxisGroup } from 'enums'
import messageUtil from 'utils/messageUtil'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { getAllMeasureTypes } from 'utils/summaryUtil'
import * as measureTypeUtil from 'utils/measureTypeUtil'
import { fromSearchResult } from 'utils/timelineDataUtil'
import { getSearchTopics, WidgetInfo } from 'utils/widgetUtil'
import {
  getSeriesType,
  getStacking,
  getYAxisGroup,
  getYAxisInfo,
  getZIndex
} from 'utils/highchartsUtil'
import { addTz } from 'utils/timeZoneUtil'
import LineChart from 'components/visualization/Line'
import { LineSeries, YAxisInfo } from 'components/visualization/Line/types'
import ErrorMessage from 'components/share/ErrorMessage'
import { WidgetId } from 'features/types'
import { LineProps } from 'components/visualization/Line/types/lineConfigs'
import { getAnnotationColor } from 'components/insightContent/Annotation/utils'
import { useDisplayTheme } from 'hooks/useUiState'
import { useAppSelector } from 'app/hooks'
import { selectAnnotationsByTopicID } from 'features/topicAnnotations/selectors'

import { getSeriesColor } from '../../../utils'
import { filterResultByPermission, getSeriesLabel, canDisplayAnnotation } from '../../utils'
import { TimelineWidget, TimelineGroup, SimplifiedAnnotationsMap } from '../../../types'
import AnnotationTooltipsPortal from './AnnotationTooltipsPortal'

const isTimelineWidget = (widgetSetting: TimelineGroup): widgetSetting is TimelineWidget =>
  'viewOption' in widgetSetting

const getViewOption = (widgetSetting: TimelineGroup) => {
  if (!isTimelineWidget(widgetSetting)) {
    return undefined
  }

  return widgetSetting.viewOption
}

const getMetricNames = (widgetSetting: TimelineGroup) => {
  if (!isTimelineWidget(widgetSetting)) {
    return []
  }

  return widgetSetting.metricNames || []
}

interface TimelineProps {
  widgetId: WidgetId
  onClickChart?: LineProps['onClickChart']
  onClickPoint?: LineProps['onClickPoint']
  onSelection?: LineProps['onSelection']
}

const Timeline = ({ widgetId, onClickChart, onClickPoint, onSelection }: TimelineProps) => {
  const widgetSetting = useDashboardWidgetSetting<TimelineGroup>(widgetId)
  const widgetData = useDashboardWidgetData<AnnotatedMetricResult>(widgetId)
  const pageConfigUser = usePageConfigUser()
  const mode = useDisplayTheme()
  const lineChartRef = useRef(null)
  const annotations = useAppSelector((state) =>
    selectAnnotationsByTopicID(state, widgetSetting.topicInfos![0].topicId!)
  )
  const hasAnnotationUi = canDisplayAnnotation(widgetSetting)
  const { data } = widgetData

  const filteredData = filterResultByPermission(data, pageConfigUser.userPermissions)

  let settingMeasures
  let settingMetricNames: string[] | undefined
  let forceOffStackingOnChildMeasures = false
  if (
    getViewOption(widgetSetting) === InsightViewOption.OVERVIEW &&
    widgetSetting.measureTypes!.length === 1
  ) {
    settingMeasures = getAllMeasureTypes(
      widgetSetting.analysisType!,
      getViewOption(widgetSetting)!,
      widgetSetting.measureTypes as MeasureTypeStrings[]
    )
    if (widgetSetting.measureTypes![0] === MeasureType.NET_SENTIMENT) {
      forceOffStackingOnChildMeasures = true
    }
  } else {
    settingMeasures = widgetSetting.measureTypes || []
    settingMetricNames = getMetricNames(widgetSetting) as string[]
  }

  let settingSeriesType: string[] = []
  for (let i = 0; i < settingMeasures.length; i += 1) {
    settingSeriesType = settingSeriesType.concat(
      measureTypeUtil.getMetricSeries(settingMeasures[i]!)
    )
  }
  if (settingMetricNames && settingMetricNames.length > 0) {
    settingSeriesType = settingSeriesType.concat(settingMetricNames!)
  }
  // convert search result
  const timelineData = fromSearchResult(
    filteredData,
    getSearchTopics(widgetSetting as WidgetInfo),
    widgetSetting.timeUnit!,
    settingSeriesType,
    getViewOption(widgetSetting),
    pageConfigUser.timeZone
  )

  const measureResult = fromSearchResult(
    filteredData,
    getSearchTopics(widgetSetting as WidgetInfo),
    widgetSetting.timeUnit!,
    [MetricSeries.TOTAL_BUZZ],
    null,
    pageConfigUser.timeZone
  )

  // xCategories
  const { columns, series: seriesSet } = timelineData
  const formattedSeriesSet: LineSeries[] = []
  const SeriesColorSettings: { [key: string]: string } = {}
  const yAxisInfoList: YAxisInfo[] = []
  const notSupportedTopicIds = []
  const xAxis = columns?.map((column) => addTz(column!, pageConfigUser.timeZone))

  seriesSet!.forEach((series) => {
    let yAxisGroup = getYAxisGroup(
      series.type as MetricSeriesStrings,
      series.category!,
      widgetSetting.analysisType!,
      getViewOption(widgetSetting)
    )
    const yAxisInfo = getYAxisInfo(
      series.type as MetricSeriesStrings,
      series.category!,
      widgetSetting.analysisType!,
      getViewOption(widgetSetting)
    )

    let found = false
    if (yAxisGroup === YAxisGroup.NONE) {
      yAxisGroup = YAxisGroup.SW_BUZZ
      found = true
    } else {
      const yAxisInfoIdx = yAxisInfoList.findIndex((yAxisInfoObj) => yAxisGroup === yAxisInfoObj.id)
      if (yAxisInfoIdx > -1) {
        found = true
        if (
          yAxisInfoList[yAxisInfoIdx].title.text !==
          measureTypeUtil.getLabel(
            series.type as MeasureTypeStrings | MetricCategoryItemStrings | MetricSeriesStrings,
            widgetSetting.analysisType
          )
        ) {
          yAxisInfoList[yAxisInfoIdx].title.text = messageUtil.timelineYAxisGroupTitle(yAxisGroup)!
        }
      }
    }
    if (!found) {
      // only one yAxis will stay on the left
      if (yAxisInfoList.length === 0) yAxisInfo.opposite = false
      yAxisInfoList.push(yAxisInfo)
    }

    // generate series
    const seriesData = series.data!.map((yData, idx) => ({
      x: xAxis![idx],
      y: series.isNegative && yData !== undefined && yData !== 0 ? yData * -1 : yData // To prevent negative zero '-0'
    }))

    // CB-20806, New Summary Metrics | Suggest to disable the timeline for
    // the "-" in children metrics
    let isDash = false
    if (
      series.isChild &&
      ([
        MetricSeries.IMPRESSIONS,
        MetricSeries.NET_SENTIMENT,
        MetricSeries.PASSION
      ] as string[]).includes(series.type!)
    ) {
      if (measureResult && measureResult.series) {
        for (let j = 0; j < measureResult.series.length; j += 1) {
          if (measureResult.series[j].category === series.category) {
            if (measureResult.series[j].data) {
              const measureData = measureResult.series[j].data
              const measureString = measureData!.join('')
              isDash = measureData!.length === measureString.length && !/[^0]/.exec(measureString)
            }
            break
          }
        }
      }
    }

    const isHideParent =
      ([
        AnalysisType.SW_NEW_SUMMARY,
        AnalysisType.AM_NEW_SUMMARY,
        AnalysisType.YT_SUMMARY
      ] as string[]).includes(widgetSetting.analysisType!) &&
      !series.isChild &&
      seriesSet!.length > 1 &&
      (widgetSetting.measureTypes?.length || 0) + getMetricNames(widgetSetting).length === 1

    const isVisible = series.isSupported && !series.isNoCount && !isDash && !isHideParent

    const formattedSeries: LineSeries = {
      name: getSeriesLabel(series, widgetSetting)!,
      data: seriesData!,
      yAxis: yAxisGroup,
      type: getSeriesType(series),
      stacking: getStacking(series, forceOffStackingOnChildMeasures),
      zIndex: getZIndex(getSeriesType(series)),
      visible: isVisible!
    }
    formattedSeriesSet.push(formattedSeries)

    const seriesColor = getSeriesColor(
      series,
      widgetSetting.analysisType!,
      getViewOption(widgetSetting)
    )
    if (seriesColor) {
      SeriesColorSettings[formattedSeries.name] = seriesColor
    }

    if (!series.isSupported || series.isNoCount) {
      notSupportedTopicIds.push(series.topicId)
    }
  })

  const getClosestXFromXAxias = (time: number) => {
    let distance: null | number = null

    const closestTime = xAxis?.reduce((acc, xAxiasTime) => {
      const curDistance = time - xAxiasTime
      if (distance === null || (curDistance < distance && curDistance >= 0)) {
        distance = curDistance
        return xAxiasTime
      }
      return acc
    }, xAxis?.[0] ?? 0)

    return closestTime || time
  }

  const simplifiedAnnotationsMap = hasAnnotationUi
    ? annotations.reduce((acc: SimplifiedAnnotationsMap, annotation) => {
        const key = addTz(annotation.startDatetime!, pageConfigUser.timeZone)
        if (
          xAxis &&
          (annotation.endDatetime! < xAxis[0] ||
            annotation.startDatetime! > xAxis[xAxis.length - 1])
        )
          return acc

        const closestKey = getClosestXFromXAxias(key)?.toString()

        if (acc[closestKey]) {
          const annotationColor = getAnnotationColor(annotation.color!, mode)
          acc[closestKey].count = Math.min(acc[closestKey].count! + 1, 99)
          acc[closestKey].color =
            acc[closestKey].color === annotationColor
              ? annotationColor
              : getAnnotationColor(NBColor.GREY, mode)

          acc[closestKey].meta!.annotations.push(annotation)
        } else {
          acc[closestKey] = {
            x: Number(closestKey),
            count: 1,
            color: getAnnotationColor(annotation.color!, mode),
            meta: {
              annotations: [annotation]
            }
          }
        }
        return acc
      }, {})
    : {}

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorMessage>{error.message}</ErrorMessage>}>
      <LineChart
        ref={lineChartRef}
        series={formattedSeriesSet}
        timeUnit={widgetSetting.timeUnit!.toLowerCase() as TimeUnit}
        yAxisTitle={yAxisInfoList}
        seriesHeader=""
        colorSettings={SeriesColorSettings}
        onClickChart={onClickChart}
        onClickPoint={onClickPoint}
        onSelection={onSelection}
        simplifiedAnnotations={Object.values(simplifiedAnnotationsMap)}
      />
      {hasAnnotationUi && (
        <AnnotationTooltipsPortal
          simplifiedAnnotationsMap={simplifiedAnnotationsMap}
          chartRef={lineChartRef}
        />
      )}
    </ErrorBoundary>
  )
}

export default memo(Timeline)
